import { useEffect, useState } from 'react';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Alert } from '@/components/UI/Alert';
import { Modal } from '@/components/UI/Modal';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import {
  useListAppLogsQuery,
  type AppLogEntry,
  type LogLevel,
} from '@/redux/internal-services/platformLogsApi';
import {
  DetailRow,
  JsonBlock,
  LevelBadge,
  PAGE_SIZE,
  PagedFooter,
  TraceLink,
  fmtTime,
  toIso,
  type SharedLogFilters,
} from './shared';

const LEVELS: LogLevel[] = ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];

/** Debounce free-text filters so typing does not fire a request per keystroke. */
function useDebounced(value: string, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function AppLogTab({ tenantId, fromDate, toDate, onTrace }: SharedLogFilters) {
  const [levels, setLevels] = useState<LogLevel[]>(['WARNING', 'ERROR', 'CRITICAL']);
  const [searchInput, setSearchInput] = useState('');
  const [loggerInput, setLoggerInput] = useState('');
  const [userId, setUserId] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<AppLogEntry | null>(null);
  const search = useDebounced(searchInput);
  const logger = useDebounced(loggerInput);

  useEffect(() => setSkip(0), [tenantId, fromDate, toDate, levels, search, logger, userId]);

  const { data, isFetching, isError, refetch } = useListAppLogsQuery({
    tenant_id: tenantId || undefined,
    from_date: toIso(fromDate),
    to_date: toIso(toDate),
    level: levels,
    search: search || undefined,
    logger: logger || undefined,
    user_id: userId || undefined,
    limit: PAGE_SIZE,
    skip,
  });
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const toggle = (lv: LogLevel) =>
    setLevels((cur) => (cur.includes(lv) ? cur.filter((x) => x !== lv) : [...cur, lv]));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {LEVELS.map((lv) => (
          <Chip key={lv} selected={levels.includes(lv)} onClick={() => toggle(lv)}>
            {lv}
          </Chip>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input aria-label="Search message text" placeholder="Search message…" value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-56')} />
        <input aria-label="Filter by logger" placeholder="Logger (prefix)" value={loggerInput}
          onChange={(e) => setLoggerInput(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-44')} />
        <input aria-label="Filter by user ID" placeholder="User ID" value={userId}
          onChange={(e) => setUserId(e.target.value)} className={cn(fieldClass({ size: 'sm' }), 'w-48')} />
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
        <span className="text-xs text-ui-text3">{total} results</span>
      </div>

      {isError && <Alert tone="danger">Could not load application logs.</Alert>}

      <div className="table-card">
        {isFetching ? (
          <LoadingState label="Loading..." />
        ) : rows.length === 0 ? (
          <EmptyState title="No log lines match these filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th-cell">Time</th>
                  <th className="th-cell">Level</th>
                  <th className="th-cell">Logger</th>
                  <th className="th-cell">Message</th>
                  <th className="th-cell">Tenant</th>
                  <th className="th-cell">Trace</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="tr-hover cursor-pointer" onClick={() => setSelected(r)}>
                    <td className="td-cell whitespace-nowrap text-xs text-ui-text2">{fmtTime(r.timestamp)}</td>
                    <td className="td-cell"><LevelBadge level={r.level} /></td>
                    <td className="td-cell font-mono text-xs text-ui-text3">{r.logger}</td>
                    <td className="td-cell max-w-xl text-xs text-ui-text">
                      <span className="line-clamp-2 break-all">{r.message}</span>
                      {r.exception && <span className="text-danger-text"> · has stack trace</span>}
                    </td>
                    <td className="td-cell font-mono text-xs text-ui-text3">{r.tenant_id || '—'}</td>
                    <td className="td-cell" onClick={(e) => e.stopPropagation()}>
                      <TraceLink id={r.correlation_id} onTrace={onTrace} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PagedFooter skip={skip} total={total} onSkip={setSkip} />

      {selected && (
        <Modal title="Log line" onClose={() => setSelected(null)} maxWidthClassName="sm:max-w-3xl">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailRow label="Time">{fmtTime(selected.timestamp)}</DetailRow>
            <DetailRow label="Level"><LevelBadge level={selected.level} /></DetailRow>
            <DetailRow label="Source">
              {selected.logger} · {selected.func}:{selected.line}
            </DetailRow>
            <DetailRow label="Tenant">{selected.tenant_id}</DetailRow>
            <DetailRow label="User">{selected.user_id}</DetailRow>
            <DetailRow label="Trace">
              <TraceLink id={selected.correlation_id} onTrace={(id) => { setSelected(null); onTrace(id); }} />
            </DetailRow>
          </div>
          <JsonBlock value={selected.message} />
          {selected.exception && (
            <>
              <span className="eyebrow">Stack trace</span>
              <JsonBlock value={selected.exception} />
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
